<script lang="ts">
// The bell renders in both DesktopDrawer and MobileAppBar, which briefly coexist
// while resizing across the breakpoint, so a shared id would make
// ActivityFeedModal anchor its popover to the wrong element.
let activatorInstanceId = 0;
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import NavbarIcon from '@/components/Shared/AppBar/DesktopDrawer/NavbarIcon.vue';
import ActivityFeedModal from '@/components/ActivityFeed/ActivityFeedModal/ActivityFeedModal.vue';
import { ActivityFeedService } from '@/core/activity-feed/activity-feed.service';
import BellOutlinedIcon from '@/assets/svg/bell-outlined.svg';
import BellFilledIcon from '@/assets/svg/bell-filled.svg';

// Service Instantiation
// ================================
const activityFeedService = new ActivityFeedService();

// Reactive Properties
// ================================
activatorInstanceId += 1;
const bellActivatorId = ref(`activity-feed-bell-activator-${activatorInstanceId}`);
const isModalOpen = ref(false);

// Computed Properties
// ================================
const unreadMessageCount = computed(() => activityFeedService.unreadMessageCount);
const showsMessageCount = computed(() => unreadMessageCount.value > 0);
const selectedBellIcon = computed(() => (isModalOpen.value ? BellFilledIcon : BellOutlinedIcon));

// Methods
// ================================
function handleIsOpen(value: boolean) {
  isModalOpen.value = value;
}

// Lifecycle Hooks
// ================================
onMounted(() => {
  activityFeedService.startPollingUnreadMessageCount();
});

onBeforeUnmount(() => {
  activityFeedService.stopPollingUnreadMessageCount();
});
</script>

<template>
  <ActivityFeedModal :target="`#${bellActivatorId}`" @isOpen="handleIsOpen">
    <template #activator>
      <div :id="bellActivatorId" class="relative cursor-pointer">
        <NavbarIcon title="Activity Feed" :src="selectedBellIcon" :selected-src="BellFilledIcon" />
        <div
          v-if="showsMessageCount"
          class="absolute right-2 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[#F83D6A] text-[8px] font-extrabold text-tint-0"
        >
          {{ unreadMessageCount }}
        </div>
      </div>
    </template>
  </ActivityFeedModal>
</template>
